import { useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import ToolsBreadcrumb from '@/app/common/components/ToolsBreadcrumb';
import ToolTabs from '@/app/common/components/ToolTabs';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import ExpireCheck from '@/app/common/components/ExpireCheck';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';
import RouteHelperAlerts from '@/components/common/RouteHelperAlerts';
import { ProjectProvider, useProject } from './lib/project-context';
import { OnboardingGuard, useOnboardingNavHidden } from './lib/onboarding-guard';
import { useIndexerPaths } from './lib/indexer-paths';
import { getPageGuide, type PageGuideKey } from './lib/page-guides';
import { IndexerShellPreload } from './components/preloads/IndexerPreloads';
import './styles.css';

const NAV_ITEMS: {
  name: string;
  page: 'overview' | 'index' | 'url-inspection' | 'urls' | 'changes' | 'settings';
  guideKey: PageGuideKey;
}[] = [
  { name: 'نمای کلی', page: 'overview', guideKey: 'overview' },
  { name: 'درخواست ایندکس', page: 'index', guideKey: 'index' },
  { name: 'وضعیت ایندکس', page: 'url-inspection', guideKey: 'inspect' },
  { name: 'لیست آدرس‌ها', page: 'urls', guideKey: 'urls' },
  { name: 'تغییرات', page: 'changes', guideKey: 'changes' },
  { name: 'تنظیمات', page: 'settings', guideKey: 'settings' },
];

function IndexerShell() {
  const paths = useIndexerPaths();
  const location = useLocation();
  const hideNav = useOnboardingNavHidden();
  const { loading } = useProject();

  const activeNav = useMemo(
    () =>
      NAV_ITEMS.find((nav) =>
        location.pathname.includes(`/indexer/${nav.page}/`),
      ) ?? null,
    [location.pathname],
  );

  const activeRouteHelp = useMemo(() => {
    if (!activeNav) return null;
    return {
      name: activeNav.name,
      to: `/indexer/${activeNav.page}`,
      helper: getPageGuide(activeNav.guideKey).shortAlert,
    };
  }, [activeNav]);

  if (loading) {
    return <IndexerShellPreload pathname={location.pathname} />;
  }

  return (
    <div className="indexer-panel-root">
      <ToolsBreadcrumb
        parentName="ابزار ایندکسر"
        parentUrl={paths.overview}
        childs={
          activeNav && !hideNav
            ? [{ text: activeNav.name, href: paths.page(activeNav.page) }]
            : hideNav
              ? [{ text: 'راه‌اندازی', href: paths.onboarding }]
              : []
        }
      />

      {!hideNav && (
        <ToolTabs
          items={NAV_ITEMS.map((item) => ({
            key: item.page,
            label: item.name,
            to: paths.page(item.page),
            active: location.pathname.includes(`/indexer/${item.page}/`),
          }))}
        />
      )}

      {!hideNav && activeRouteHelp?.helper ? (
        <RouteHelperAlerts route={activeRouteHelp} />
      ) : null}

      <Outlet />
    </div>
  );
}

export default function IndexerLayout() {
  const access = useToolAccess('indexer');

  if (access.isLoading) return <></>;

  return (
    <ExpireCheck>
      {!access.haveAccess ? (
        <ToolAccessDenied access={access} tool_label="ابزار ایندکسر" />
      ) : shouldShowDataLoadError(access.isError, access.error) ? (
        <DataLoadError
          onRetry={() => access.refetch()}
          isRetrying={access.isFetching}
          isNetworkError={isNetworkApiError(access.error)}
        />
      ) : (
        <ProjectProvider>
          <OnboardingGuard>
            <IndexerShell />
          </OnboardingGuard>
        </ProjectProvider>
      )}
    </ExpireCheck>
  );
}
